﻿import { CircularProgress, Grid } from "@material-ui/core";
import {
	DndProvider,
	type DragLayerMonitorProps,
	type DropOptions,
	type NodeModel,
	Tree,
} from "@minoru/react-dnd-treeview";
import React, { useEffect, useMemo, useState } from "react";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ErrorModal } from "../../../../../../../components/modals/error-modal";
import { useTrendingContext } from "../../../context/TrendingContext";
import { TagFromTree } from "../../../models/TagsTree.model";
import {
	changeTagFolderToTag,
	getTagsTree,
	upsertTagFolder,
} from "../../../repository/TrendingRepository";
import { useTagsTreeModalStyles } from "../../../styles/TasgTreeModalStyles";
import { buildTagsTreeV2 } from "../../../utils/buildTagsTree";
import { ContextMenu } from "./ContextMenu/ContextMenu";
import { CustomDragPreview } from "./CustomDragView";
import { TagFolderCustomNode } from "./TagFolderCustomNode";

interface TagSelectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTagSelect: (tag: TagFromTree) => void;
}

export const TagSelectionModal = ({
	isOpen,
	onClose,
	onTagSelect,
}: TagSelectionModalProps) => {
	const classes = useTagsTreeModalStyles();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");

	const [tagsTreeData, setTagsTreeData] = useState<NodeModel<TagFromTree>[]>(
		[],
	);
	const [tagNodeSelectedInTree, setTagNodeSelectedInTree] =
		useState<NodeModel<TagFromTree> | null>(null);
	const [anchorPoint, setAnchorPoint] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [showContextMenu, setShowContextMenu] = useState(false);

	const backendOffset = 1000000;

	const [openTagFolders, setOpenTagFolders] = useState<{
		[TagId: string]: string | number;
		[TagId: number]: string | number;
	}>({});
	const [selectedNodes, setSelectedNodes] = useState<NodeModel<TagFromTree>[]>(
		[],
	);

	const {
		state: { viewTags },
	} = useTrendingContext();

	const handleContextMenu = (
		data: NodeModel<TagFromTree>,
		e: React.MouseEvent<HTMLElement>,
	) => {
		e.preventDefault();
		const top = e.clientY;
		const left = e.clientX;

		setAnchorPoint({ x: left, y: top });

		setSelectedNodes([data]);

		setShowContextMenu(true);
		const selectedNode = tagsTreeData.find(
			(node: NodeModel<TagFromTree>) => node.data?.TagId === data.id,
		);
		if (selectedNode) {
			setTagNodeSelectedInTree(selectedNode);
		}
	};

	const getTagsTreeData = async () => {
		if (isOpen) {
			setIsLoading(true);
			const tagsTree = await getTagsTree();
			if (tagsTree.ok) {
				const rows = tagsTree.data.tables[0].rows;
				const tree = buildTagsTreeV2(rows);
				setTagsTreeData(tree);
			}
			setIsLoading(false);
		}
	};

	const expanded = useMemo(() => {
		return Object.keys(openTagFolders).map((el) => Number.parseInt(el));
	}, [openTagFolders]);

	useEffect(() => {
		getTagsTreeData();
	}, [isOpen]);

	const handleDrop = async (
		tree: NodeModel<TagFromTree>[],
		options: DropOptions<TagFromTree>,
	) => {
		setIsLoading(true);
		if (options.dragSource) {
			const tagId = Number(options.dragSource.id);
			const newFolderId = Number(options.dropTarget?.id) || null;

			if (tagId === newFolderId) {
				setIsLoading(false);
				return;
			}
			if (options.dragSource.data?.TagTypeCode === "F") {
				const newTree = await upsertTagFolder(
					tagId,
					newFolderId,
					options.dragSource.text,
					true,
				);
				if (!newTree.ok) {
					setError(newTree.message);
				}
			} else {
				const newTree = await changeTagFolderToTag(tagId, newFolderId);
				if (!newTree.ok) {
					setError(newTree.message);
				}
			}
			setTagsTreeData(tree);
		}
		setIsLoading(false);
	};

	const handleClick = (node: NodeModel<TagFromTree>) => {
		if (node.data) {
			onTagSelect(node.data);
		}
	};

	const handleCloseContextMenu = (e: any) => {
		if (showContextMenu) {
			setShowContextMenu(false);
		}
	};

	return (
		<>
			<div
				style={{
					display: isOpen ? "block" : "none",
					minHeight: "700px",
					minWidth: "800px",
				}}
				onClick={handleCloseContextMenu}
			>
				<div style={{ overflowY: "auto", maxHeight: "700px" }}>
					{isLoading ? (
						<Grid
							container
							justifyContent="center"
							alignItems="center"
							style={{ minHeight: "100px" }}
						>
							<CircularProgress size="2rem" />
						</Grid>
					) : (
						<DndProvider backend={HTML5Backend}>
							<Tree
								classes={{
									root: classes.assetRoot,
									dropTarget: classes.dropTarget,
								}}
								initialOpen={expanded}
								tree={tagsTreeData}
								rootId={backendOffset}
								onDrop={handleDrop}
								canDrop={(
									tag: any,
									options: DropOptions<TagFromTree>,
								): boolean => {
									return !selectedNodes.some(
										(selectedNode) =>
											Number(selectedNode.id) === Number(options.dropTargetId),
									);
								}}
								render={(node, options) => {
									const selected = selectedNodes.some(
										(selectedNode) =>
											Number(selectedNode.id) === Number(node.id),
									);
									return (
										<TagFolderCustomNode
											setContextMenuOver={handleContextMenu}
											node={node}
											options={options}
											isSelected={selected}
											openTagFolders={openTagFolders}
											setOpenTagFolders={setOpenTagFolders}
											handleSelect={handleClick}
										/>
									);
								}}
								dragPreviewRender={(
									monitorProps: DragLayerMonitorProps<TagFromTree>,
								) => (
									<CustomDragPreview monitorProps={monitorProps} />
									// <div>{monitorProps.item.data?.TagName}</div>
								)}
							/>
						</DndProvider>
					)}
				</div>
			</div>
			<ContextMenu
				anchorPoint={anchorPoint}
				isOpen={showContextMenu}
				setShowContextMenu={setShowContextMenu}
				tagNodeSelectedInTree={tagNodeSelectedInTree}
				getTagsTreeData={getTagsTreeData}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
